import { Assert } from '@cotera/utilities';
import { BaseModel } from '../../actions/framework';
import { DG } from '@cotera/sdk/core';
import { TargetableModel } from '../../actions/framework/types';
import {
  ReadonlyWatchable,
  StalenessWatchable,
  StaleState,
  Watchable,
} from '../../actions/framework/watchable';
import { FilterGroup } from '@cotera/client/app/components/app/filter-builder/types';
import {
  filterGroupToExpression,
  makeDefaultFilterGroup,
} from '@cotera/client/app/components/app';
import { Constant, Expression, Relation } from '@cotera/era';
import { err, ok, Result } from 'neverthrow';
import { v4 } from 'uuid';
import { GraphModel } from '../../graph/graph.model';

export class DatasetModel extends BaseModel implements TargetableModel {
  private _isTargeted: boolean = false;

  /**
   * The state of the user-supplied values for the dataset's name and eraQL.
   *
   * Initially empty strings if new (or whatever the previously saved/staged values are if not new).
   */
  readonly formState: Watchable<{
    name: string;
    eraql: string;
  }>;

  /**
   * The compiled item in the "staged" graph that this dataset refers to.
   *
   * TODO: should this be the buffered graph or staged graph???
   */
  readonly item: ReadonlyWatchable<DG.Graph.RelItem | null>;
  readonly status: ReadonlyWatchable<'ok' | 'error' | 'empty'>;

  /**
   * The identity of this dataset.
   *
   * null if new
   */
  readonly datasetIdentity: Watchable<DG.Graph.Identity | null>;

  /**
   * Current name in either the "staged" graph and the "buffered" graph.
   *
   * Null indicates new
   *
   * It is not possible for the name to be different in either graph because
   * initiating a re-name automatically stages (and if a subset of ops in the stage
   * is unsuccessful, the rename is guaranteed to succeed).
   */
  readonly currentNameInGraph: ReadonlyWatchable<string | null>;

  readonly config: ReadonlyWatchable<DG.Graph.RelNode | null>;

  /**
   * The compiled graph, taking into account:
   *  - published commits
   *  - saved/staged operations
   */
  readonly graph: ReadonlyWatchable<DG.BuildExplorer>;

  /**
   * The compiled graph, taking into account:
   *  - published commits
   *  - saved/staged operations
   *  - buffered (pre-save/pre-staged) operations.
   */
  readonly bufferedGraph: ReadonlyWatchable<DG.BuildExplorer>;

  /**
   * Any errors that occured since compiling the last graph (including
   * pre-save/pre-stage ops)
   *
   * (empty list may also indicate that dataset is new)
   */
  readonly errors: ReadonlyWatchable<DG.Graph.GraphError[]>;

  /**
   * The pre-save/pre-stage operations.
   */
  readonly newOperations = Watchable.fromValue<DG.OperationData[]>([]);

  /**
   * A boolean record indicating which aspects of the dataset have changed.
   */
  readonly changes: ReadonlyWatchable<{
    eraql: boolean;
    where: boolean;
  }>;

  readonly filterGroup: Watchable<FilterGroup>;
  readonly whereEraql: ReadonlyWatchable<string | null>;
  readonly whereExpr: ReadonlyWatchable<Expression | null>;

  readonly isEditorCollapsed: Watchable<boolean>;

  readonly preview: Watchable<Relation | null>;

  /**
   * A compiler loaded with past commits, staged ops, AND also pre-save/pre-stage operations.
   */
  private readonly internalCompiler: ReadonlyWatchable<DG.GraphCompiler>;
  readonly isPreviewStale: ReadonlyWatchable<StaleState>;
  readonly hasBeenTargeted: Watchable<boolean>;
  readonly route: ReadonlyWatchable<string>;
  readonly pageTitle: ReadonlyWatchable<string>;

  /**
   *
   * @param inputs
   *  - graph: a watchable of a graph of "staged" operations
   *  - compiler: a watchable of a compiler loaded with "staged" operations
   * @param opts
   */
  constructor(
    inputs: {
      name: string | null;
      graph: ReadonlyWatchable<DG.BuildExplorer>;
      compiler: ReadonlyWatchable<DG.GraphCompiler>;
    },
    private readonly opts: {
      id?: string;
      targeted?: boolean;
      isNew?: boolean;
      startingEraQl?: string;
    } = {}
  ) {
    super(opts.id ?? inputs.name ?? 'new-dataset-' + v4());

    this._isTargeted = opts.targeted ?? false;

    this.graph = inputs.graph;

    this.internalCompiler = Watchable.from((get) => {
      const baseCompiler = get(inputs.compiler);

      const unappliedOps = get(this.newOperations);

      return baseCompiler.commit({
        actorId: v4(),
        message: null,
        operations: [...unappliedOps],
        timestamp: new Date(),
      });
    });

    this.bufferedGraph = Watchable.from((get) => {
      const compiler = get(this.internalCompiler);

      const graph = compiler.compile().match(
        (x) => x,
        (x) => x.lastGoodBuild
      );

      return graph;
    });

    const datasetNode =
      inputs.name && inputs.graph.snapshot().nodeMap[inputs.name];
    this.datasetIdentity = Watchable.fromValue(
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      datasetNode ? datasetNode.meta.identity : null
    );

    this.currentNameInGraph = Watchable.from((get) => {
      const datasetIdentity = get(this.datasetIdentity);

      if (datasetIdentity === null) {
        return null;
      }

      const stagedGraph = get(this.graph);

      /** Graph with saved/staged operations */
      const currentNameInStagedGraph = getNameInGraph(
        datasetIdentity,
        stagedGraph
      );

      return currentNameInStagedGraph;
    });

    // Assert that the nullity reflects the caller-intended new-ness of this Dataset.
    if (opts.isNew) {
      Assert.assert(this.currentNameInGraph.snapshot() === null);
    } else {
      Assert.assert(this.currentNameInGraph.snapshot() !== null);
    }

    this.item = Watchable.from((get) => {
      const name = get(this.currentNameInGraph);
      if (name === null) {
        return null;
      }

      const graph = get(this.graph);

      const graphResult = Assert.assertExists(graph.graph[name]);
      if (graphResult.isErr()) {
        return null;
      }

      const item = graphResult.value.item;
      Assert.assert(item.t === 'rel');

      return {
        ...item,
        rel: item.rel,
      };
    });

    const originalConfig = Watchable.from((get): DG.Graph.RelNode | null => {
      const name = get(this.currentNameInGraph);
      if (name === null) {
        return null;
      }

      const stagedGraph = get(this.graph);

      const graphNode = Assert.assertExists(stagedGraph.nodeMap[name]);
      Assert.assert(graphNode.t === 'rel');

      const eraqlWithWhere = graphNode.where
        ? graphNode.source + ` |>  Where(${graphNode.where})`
        : graphNode.source;

      return {
        ...graphNode,
        source: eraqlWithWhere,
      };
    });

    //this is a temp hack to let the eraql work with old stored where expressions

    this.config = Watchable.from((get): DG.Graph.RelNode | null => {
      const datasetIdentity = get(this.datasetIdentity);
      if (datasetIdentity === null) {
        return null;
      }

      const bufferedGraph = get(this.bufferedGraph);
      const nodes = Object.values(bufferedGraph.nodeMap);

      const node = nodes.find((node) => node.meta.identity.eq(datasetIdentity));
      Assert.assert(node !== undefined);
      Assert.assert(node.t === 'rel');

      return node;
    });

    this.errors = Watchable.from((get) => {
      const datasetIdentity = get(this.datasetIdentity);
      if (datasetIdentity === null) {
        // dataset is new => ie no errors in graph yet.
        return [];
      }

      const bufferedGraph = get(this.bufferedGraph);
      const rawNodeEntries = Object.entries(bufferedGraph.nodeMap);

      const [currentNameInBufferedGraph, _rawNode] = Assert.assertExists(
        rawNodeEntries.find(([_, node]) =>
          node.meta.identity.eq(datasetIdentity)
        )
      );

      const builtNode = Assert.assertExists(
        bufferedGraph.graph[currentNameInBufferedGraph]
      );

      return builtNode.match(
        () => [],
        (e) => [e.err]
      );
    });

    this.formState = Watchable.fromValue({
      name: (this.currentNameInGraph.snapshot() || inputs.name) ?? '',
      eraql: originalConfig.snapshot()?.source ?? opts.startingEraQl ?? '',
    });

    this.status = Watchable.from((get) => {
      const initalEraQl = get(this.formState)?.eraql;

      const empty = (initalEraQl?.length ?? 0) === 0;
      const isErrored = get(this.errors).length > 0;

      if (empty) {
        return 'empty';
      }

      if (!empty && isErrored) {
        return 'error';
      }

      return 'ok';
    });

    this.isEditorCollapsed = Watchable.fromValue(
      this.status.snapshot() === 'ok'
    );

    this.filterGroup = Watchable.fromValue(makeDefaultFilterGroup());

    this.whereExpr = Watchable.from((get) => {
      const item = get(this.item);
      if (item === null) {
        return null;
      }

      const builtRel = item.rel;

      return filterGroupToExpression(builtRel.ref('from'))(
        get(this.filterGroup)
      );
    });

    this.whereEraql = Watchable.from((get) => {
      return get(this.whereExpr)?.toEraQL() ?? null;
    });

    const item = this.item.snapshot();

    //we do NOT want this to be updated automatically when the symbol changes
    //it has to be a choice, which is why this is not a derived watchable
    this.preview = Watchable.fromValue(item === null ? null : item.rel);

    this.changes = Watchable.from((get) => {
      const originalConfigSnapshot = get(originalConfig);

      return {
        eraql: get(this.formState).eraql !== originalConfigSnapshot?.source,
        where: get(this.whereEraql) !== 'true',
      };
    });

    this.isPreviewStale = StalenessWatchable.forValue(this.preview, [
      this.graph,
    ]);

    this.hasBeenTargeted = Watchable.fromValue(false);

    this.route = Watchable.from(
      (get) => `/dataset/${encodeURIComponent(get(this.formState).name)}`
    );

    this.pageTitle = Watchable.from((get) => {
      return (get(this.currentNameInGraph) || inputs.name) ?? '';
    });
  }

  static fromGraph(
    graph: GraphModel,
    name: string,
    opts: {
      id?: string;
      targeted?: boolean;
      isNew?: boolean;
      startingEraQl?: string;
    } = {}
  ) {
    return new DatasetModel(
      {
        name,
        graph: graph.graph,
        compiler: graph.compiler,
      },
      opts
    );
  }

  onCreate(): void {
    if (this.opts.startingEraQl !== undefined) {
      this.compile({ setPreview: true });
    }
  }

  get isTargeted() {
    return this._isTargeted;
  }

  markAsStaged(): void {
    this.newOperations.set([]);
  }

  target(): void {
    this._isTargeted = true;
    this.hasBeenTargeted.set(true);
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  compile(
    props: {
      setPreview?: boolean;
      select?: (rel: Relation) => Relation;
    } = { setPreview: true }
  ): Result<
    {},
    | { t: 'build'; e: DG.CommitsBuildFailure }
    | { t: 'tc'; e: DG.Graph.GraphError }
    | { t: 'form-state-error'; e: { message: 'Dataset is missing name.' } }
    | { t: 'build'; e: { t: string; message: string } }
  > {
    if (this.formState.snapshot().name === '') {
      return err({
        t: 'form-state-error',
        e: { message: 'Dataset is missing name.' },
      });
    }

    const select = props.select ?? ((x) => x);
    const graphSnapshot = this.graph.snapshot();
    const compiler = graphSnapshot.compiler;

    const currentGraph = compiler.compile().match(
      (x) => x,
      (e) => e.lastGoodBuild
    );
    const nextOps = this.makeOpsFromState(currentGraph);

    if (nextOps.isErr()) {
      return err({ t: 'build', e: nextOps.error });
    }

    const compilationResult = compiler
      .commit({
        actorId: v4(),
        message: null,
        operations: [...nextOps.value],
        timestamp: new Date(),
      })
      .compile();

    this.newOperations.set(nextOps.value);

    if (compilationResult.isErr()) {
      return err({ t: 'build', e: compilationResult.error.err });
    }

    const relSymbol = compilationResult.value.forSymbol(
      this.formState.snapshot().name
    );

    Assert.assert(relSymbol !== null);

    if (relSymbol.builtNode.isErr()) {
      return err({ t: 'tc', e: relSymbol.builtNode.error.err });
    }

    const { item } = relSymbol.builtNode.value;

    Assert.assert(item.t === 'rel', 'Rel not found');

    if (props.setPreview) {
      const filters = this.whereExpr.snapshot() ?? Constant(true);
      this.preview.set(select(item.rel).where(() => filters));
    }

    return ok({});
  }

  dispose() {}

  get isNew() {
    return this.currentNameInGraph.snapshot() === null;
  }

  /**
   * Assumes formState.name is a non-empty string.
   */
  private makeOpsFromState(
    graph: DG.BuildExplorer
  ): Result<DG.OperationData[], { t: string; message: string }> {
    Assert.assert(this.formState.snapshot().name.length > 0);

    const currentNameInGraph = this.currentNameInGraph.snapshot();

    if (currentNameInGraph === null) {
      return ok([
        DG.Operations.createRel({
          symbol: this.formState.snapshot().name,
          source: this.formState.snapshot().eraql,
          where: null,
        }),
      ]);
    }

    const ops: DG.OperationData[] = [...this.newOperations.snapshot()];

    const nameHasChanged =
      this.formState.snapshot().name !== currentNameInGraph;
    //TODO use changes.snapshot() here
    const dataHasChanged =
      this.formState.snapshot().eraql !== this.config.snapshot()?.source;

    const rel = this.isNew
      ? null
      : graph.getRel({ symbol: currentNameInGraph });

    if (rel === null) {
      return err({
        t: 'rel-not-found',
        message: `Rel not found: ${currentNameInGraph}`,
      });
    }

    if (nameHasChanged && !this.isNew) {
      const relOps = rel.makeRenameOps({
        to: this.formState.snapshot().name,
      });

      if (relOps.isErr()) {
        return err({
          t: 'rename-failure',
          message: JSON.stringify(relOps.error),
        });
      }
      ops.push(...relOps.value);
    }

    if (dataHasChanged) {
      const relOps = rel.makeUpdateOps({
        symbol: this.formState.snapshot().name,
        source: this.formState.snapshot().eraql,
        where: null,
      });

      if (relOps.isErr()) {
        return err({ t: 'update-failure', message: relOps.error });
      }

      ops.push(...relOps.value);
    }

    return ok(ops);
  }

  toggleEditorCollapsed() {
    const curr = this.isEditorCollapsed.snapshot();
    this.isEditorCollapsed.set(!curr);
  }
}

function getNameInGraph(
  id: DG.Graph.Identity,
  graph: DG.BuildExplorer
): string | null {
  const nodes = Object.entries(graph.nodeMap);

  const graphEntry = nodes.find(([_, node]) => node.meta.identity.eq(id));

  if (graphEntry === undefined) {
    return null;
  }

  const [datasetName, _] = graphEntry;
  return datasetName;
}
